import React, { Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type Item from '../../../@types/item';
import type { PaymentListItem } from '../../../Data/Payments/usePayments';
import { numberSeparator } from '../../../helpers/formats';
import { getMarket } from '../../../i18n';
import { Align } from '../../../styled-components/components/Align';
import { Box } from '../../../styled-components/components/Box';
import { Column, Columns } from '../../../styled-components/components/Columns';
import { CreditorDisclaimer } from '../../../styled-components/components/CreditorDisclaimer';
import { LinkButton } from '../../../styled-components/components/LinkButton';
import { Price } from '../../../styled-components/components/Price';
import { Stack, StackSmall } from '../../../styled-components/components/Stack';
import { Text } from '../../../styled-components/components/Text';
import { Img } from '../../../styled-components/components/Utilities';
import { getPercentageFromFraction } from '../../../utils/finance';
import PaymentModal from './payment-modal';

interface PaymentsProps {
  id: string;
  item: Item;
  branchId: string;
  payments: PaymentListItem[];
}

const Payments = ({ id, item, branchId, payments }: PaymentsProps) => {
  const { t } = useTranslation(['item', 'currency']);
  const [paymentModal, setPaymentModal] = useState<PaymentListItem | undefined>();
  const marketCode = getMarket().marketCode;

  const onOpenPaymentModal = useCallback(
    (payment: PaymentListItem) => setPaymentModal(payment),
    [],
  );
  const onClosePaymentModal = useCallback(() => setPaymentModal(undefined), []);

  if (!payments?.length) return null;

  const imageUrl = payments?.[0].imageUrl;
  const financialInstitution = payments?.[0].financialInstitution;

  // List of financial institutions that should show the creditor disclaimer
  /*
  const disclaimerFinancialInstitutions = ['Volvofinans - en del av Ziklo Bank', 'Ziklo Bank'];

  const showCreditorDisclaimer =
    marketCode === 'SE' &&
    payments.some((payment) =>
      disclaimerFinancialInstitutions.includes(payment.financialInstitution),
    );
  */
  const showCreditorDisclaimer = marketCode === 'SE';

  return (
    <>
      {paymentModal && (
        <PaymentModal
          id={id}
          item={item}
          branchId={branchId}
          payment={paymentModal}
          showCreditorDisclaimer={showCreditorDisclaimer}
          onClose={onClosePaymentModal}
        />
      )}
      <StackSmall>
        <Box>
          {payments.map((payment, index) => (
            <Fragment key={`${payment.name}-${index}`}>
              <Stack>
                <StackSmall>
                  <Columns $spacing={2} $valign="center">
                    <Column $grow>
                      <Text $weight="medium" $size="smallStatic">
                        {payment.name}
                      </Text>
                    </Column>
                    <Column $textAlign="right" $noShrink>
                      <Price>
                        {t('monthlyCost', {
                          ns: 'currency',
                          price: payment.monthlyCost > 0 ? numberSeparator(payment.monthlyCost) : 0,
                        })}
                      </Price>
                    </Column>
                  </Columns>
                </StackSmall>
                <StackSmall>
                  <Text $tone="alt" $size="xsmallStatic">
                    {!!payment.residual?.current
                      ? t('installmentsDisclaimerWithResidual', {
                          ns: 'item',
                          amount: payment.loanAmount > 0 ? numberSeparator(payment.loanAmount) : 0,
                          duration: payment.duration.current,
                          interest: getPercentageFromFraction(payment.interest),
                          effectiveInterest: getPercentageFromFraction(payment.effectiveInterest),
                          residual: getPercentageFromFraction(payment.residual.current),
                        })
                      : t('installmentsDisclaimer', {
                          ns: 'item',
                          amount: payment.loanAmount > 0 ? numberSeparator(payment.loanAmount) : 0,
                          duration: payment.duration.current,
                          interest: getPercentageFromFraction(payment.interest),
                          effectiveInterest: getPercentageFromFraction(payment.effectiveInterest),
                        })}
                    <LinkButton
                      onClick={() => onOpenPaymentModal(payment)}
                      title={payment.financialInstitution}
                      data-wayke-action="financial-read-more"
                      $block
                    >
                      {t('adjustDetails', { ns: 'common' })}
                    </LinkButton>
                  </Text>
                </StackSmall>
              </Stack>
            </Fragment>
          ))}
          {imageUrl && (
            <Stack>
              <Align $align="center">
                <Img
                  src={`${imageUrl}?h=40`}
                  srcSet={`${imageUrl}?h=80 2x`}
                  alt={financialInstitution}
                  style={{ maxWidth: '80px' }}
                />
              </Align>
            </Stack>
          )}
        </Box>
      </StackSmall>
      {showCreditorDisclaimer && (
        <StackSmall>
          <Box>
            <CreditorDisclaimer />
          </Box>
        </StackSmall>
      )}
    </>
  );
};

export default Payments;
